<template>
  <div :class="['descuento-card', { 'notValid': !isValid }]">

    <!-- Cabecera de la tarjeta -->
    <div class="card-header position-relative">
      <div class="fs-6 fw-bold text-success w-100">
        {{ $t('configuracion_descuentos.titulo') }}
      </div>
    </div>

    <!-- Cuerpo de la tarjeta -->
    <div class="card-body">
      <div v-for="(descuento, index) in model" class="descuento-container" :key="index">
        <div class="descuento-field">
          <div class="dx-field-item-label-text">{{ $t('configuracion_descuentos.nombre') }}</div>
          <div class="dx-field-value">
            <DxTextBox v-model:value="descuento.Nombre" :read-only="readOnly">
              <DxValidator ref="validators">
                <DxRequiredRule :message="$t('configuracion_descuentos.validacion.nombre')" />
              </DxValidator>
            </DxTextBox>
          </div>
        </div>
        <div class="descuento-field">
          <div class="dx-field-item-label-text">{{ $t('configuracion_descuentos.nombre_tecnico') }}</div>
          <div class="dx-field-value">
            <DxTextBox v-model:value="descuento.NombreTecnico" :read-only="readOnly">
              <DxValidator ref="validators">
                <DxRequiredRule :message="$t('configuracion_descuentos.validacion.nombre_tecnico')" />
              </DxValidator>
            </DxTextBox>
          </div>
        </div>
        <div class="descuento-field cuantia">
          <div class="dx-field-item-label-text">{{ $t('configuracion_descuentos.cuantia') }}</div>
          <div class="dx-field-value">
            <DxNumberBox v-model:value="descuento.Cuantia" :min="0.01" :max="99.99" :step="0.01" :show-spin-buttons="true" :read-only="readOnly" :format="'##.##'">
              <DxValidator ref="validators">
                <DxRequiredRule :message="$t('configuracion_descuentos.validacion.cuantia')" />
                <DxRangeRule :min="0.01" :max="99.99" :message="$t('configuracion_descuentos.validacion.rango_cuantia')" />
              </DxValidator>
            </DxNumberBox>
          </div>
        </div>
        <div v-if="!readOnly" class="align-self-end py-1">
          <i @click="deleteDescuento(descuento)" class="fa-regular fa-trash fa-lg text-danger" role="button"></i>
        </div>
      </div>
      <DxButton :width="200" icon="plus" :text="$t('configuracion_descuentos.agregar_descuento')" :button-options="{...primaryButtonProps}" @click="addDescuento()" v-if="!readOnly" />
    </div>
    <div class="d-flex">
      <div v-if="!isValid" class="required-icon">
        <i class="fa-solid fa-circle-exclamation"></i>
      </div>
      <div v-if="!isValid" class="dx-invalid-message fw-bold">
        {{ $t('configuracion_descuentos.mensaje_invalido') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DescuentoEnergiaModel } from '@api/api';
import DxValidator, { DxRequiredRule, type DxValidator as DxValidatorType, DxRangeRule } from 'devextreme-vue/validator';
import { ref } from 'vue';
import { DxButton } from 'devextreme-vue/button';
import { DxNumberBox, DxTextBox } from 'devextreme-vue';
import { primaryButtonProps } from '@/.shared/helpers/button-helper';
import { Descuento } from '@/components/descuentos/models/Descuento';
import type { Ref } from 'vue';

const props = defineProps({
  model: Object as () => DescuentoEnergiaModel[],
  readOnly: Boolean
});

const isValid = ref(true);

const validators: Ref<DxValidatorType[]> = ref([]);

const emit = defineEmits(["update:model"]);

function addDescuento() {
  props.model?.push(new Descuento());
  validate();
}

function deleteDescuento(c: DescuentoEnergiaModel) {
  if (!props.model || !props.model) return;

  emit("update:model", props.model.filter(elem => elem !== c));
  validate();
}

const validate = async () => {
  let allValid = true;
  for (const validator of validators.value) {
    const result = validator.instance?.validate();
    if (!result?.isValid) {
      allValid = false;
    }
  }
  isValid.value = allValid;
  return { isValid: allValid };
}

defineExpose({ validate });
</script>

<style lang="scss" scoped>


.descuento-card {
  border: 1px solid var(--border-default-default);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.card-header {
  padding: 1rem 1rem;
  text-align: left;
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.card-body {
  padding: 0 1rem 1rem;
  flex: 1 1 auto;
  overflow: auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.descuento-container {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.descuento-container .descuento-field {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.descuento-container .descuento-field.cuantia {
  flex-grow: 0;
  flex-shrink: 0;
  min-width: 150px;
}

.descuento-field .dx-field-value:not(.dx-switch):not(.dx-checkbox):not(.dx-button) {
  width: 92%;
}

.info-icon {
  margin-left: auto;
  font-size: 20px;
  cursor: pointer;
  position: relative;
}

.required-icon {
  margin-left: 5px;
  font-size: 20px;
  cursor: pointer;
  position: relative;
  color: var(--text-error-default);
}

.tooltip {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: var(--background-normal-dark);
  color: var(--text-default-inverse);
  padding: 5px;
  border-radius: 4px;
  white-space: nowrap;
  font-size: 12px;
  z-index: 1000000;
  margin-top: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.requerido-tag {
  background: var(--background-primary-default);
}

.notValid {
  border: 3px solid var(--border-error-default);

  .requerido-tag {
    background: var(--background-error-default);
  }
}

.dx-invalid-message {
  display: block;
  margin: 5px 10px;
  color: var(--text-error-default);
  font-size: 12px;
}
</style>